import type { BlockRepeatMeta } from '@blocksuite/affine/model';
import type { BlockModel } from '@blocksuite/affine/store';
import { LiveData, Service } from '@toeverything/infra';
import dayjs from 'dayjs';

import type { DocsService } from '../../doc';
import type { JournalService } from '../../journal';
import { JOURNAL_DATE_FORMAT } from '../repeat-rule';

/** A journal doc, identified without loading its content. */
export type JournalDocRef = { date: string; docId: string };

export type RepeatSeriesSummary = {
  id: string;
  rule: string;
  anchor: string;
  /** Plain text of the first occurrence found, for display in menus. */
  text: string;
  count: number;
  firstDate: string;
  lastDate: string;
};

export type ApplyRepeatResult = {
  written: string[];
  failed: string[];
};

/** Flavours that carry the `repeat` prop. */
const REPEATABLE_FLAVOURS = ['affine:list', 'affine:paragraph'];

const JOURNAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** How far back `/carry` will look for a journal with open todos. */
const CARRY_LOOKBACK_JOURNALS = 14;

function readRepeat(model: BlockModel): BlockRepeatMeta | undefined {
  const repeat = (model.props as { repeat?: BlockRepeatMeta | null }).repeat;
  return repeat && typeof repeat.id === 'string' ? repeat : undefined;
}

export function today(): string {
  return dayjs().format(JOURNAL_DATE_FORMAT);
}

export class JournalRepeatService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly journalService: JournalService
  ) {
    super();
  }

  /**
   * Known repeat series, or null when never scanned. Kept warm by
   * {@link applyRepeat} and {@link deleteSeries} so the `/repeats` menu is
   * populated as soon as you create a repeat in a session.
   */
  readonly seriesIndex$ = new LiveData<RepeatSeriesSummary[] | null>(null);

  private scanPromise: Promise<RepeatSeriesSummary[]> | null = null;

  /**
   * All journal docs, ascending by date. Reads the `journal` doc property from
   * the workspace DB, so no doc content is loaded.
   */
  journalDocs(range?: { after?: string; from?: string; until?: string }) {
    const refs: JournalDocRef[] = [];
    for (const doc of this.docsService.list.docs$.value) {
      if (doc.trash$.value) continue;
      const date = doc.properties$.value.journal;
      if (typeof date !== 'string' || !JOURNAL_DATE_RE.test(date)) continue;
      if (range?.after !== undefined && date <= range.after) continue;
      if (range?.from !== undefined && date < range.from) continue;
      if (range?.until !== undefined && date > range.until) continue;
      refs.push({ date, docId: doc.id });
    }
    // YYYY-MM-DD sorts correctly as a string.
    return refs.sort((a, b) => a.date.localeCompare(b.date));
  }

  /** Creates the journal if needed and waits for its template to land. */
  private async ensureJournalReady(date: string): Promise<string> {
    const record = this.journalService.ensureJournalByDate(date);
    await this.journalService.whenJournalReady(record.id);
    return record.id;
  }

  /**
   * Copies a block into the journal entry for each date, stamping every copy
   * with the series metadata and resetting checkboxes so a recurring todo
   * arrives unticked.
   *
   * Dates are processed one at a time — each one may create a doc and wait for
   * it to sync, and firing 180 of those at once would swamp the sync layer.
   * A failure on one date is recorded and the rest continue.
   */
  async applyRepeat(params: {
    sourceDocId: string;
    blockId: string;
    dates: string[];
    repeat: BlockRepeatMeta;
    onProgress?: (done: number, total: number) => void;
  }): Promise<ApplyRepeatResult> {
    const { sourceDocId, blockId, dates, repeat, onProgress } = params;
    const written: string[] = [];
    const failed: string[] = [];

    for (const [index, date] of dates.entries()) {
      try {
        const targetDocId = await this.ensureJournalReady(date);
        await this.docsService.copyBlocksToDoc(
          sourceDocId,
          [blockId],
          targetDocId,
          {
            replaceEmptyTarget: true,
            transformSnapshot: (block, depth) => {
              // Only the top-level line carries the series id — stamping
              // children too would make deletion count them twice.
              if (depth === 0) {
                block.props.repeat = { ...repeat };
              }
              if (block.props.checked === true) {
                block.props.checked = false;
              }
            },
          }
        );
        written.push(date);
      } catch (error) {
        console.error(`Failed to repeat into journal ${date}`, error);
        failed.push(date);
      }
      onProgress?.(index + 1, dates.length);
    }

    this.refreshSeriesIndex();
    return { written, failed };
  }

  /**
   * Removes every block belonging to a series from the journals in scope.
   *
   * `skip` protects the line the command was run from, so `/delete-repeat` on
   * a future occurrence does not delete the block out from under the cursor.
   */
  async deleteSeries(params: {
    seriesId: string;
    scope: 'future' | 'all';
    fromDate?: string;
    skip?: { docId: string; blockId: string };
  }): Promise<{ removed: number; docs: number }> {
    const { seriesId, scope, skip } = params;
    const fromDate = params.fromDate ?? today();
    const refs =
      scope === 'future'
        ? this.journalDocs({ after: fromDate })
        : this.journalDocs();

    let removed = 0;
    let docs = 0;

    for (const ref of refs) {
      try {
        const count = await this.docsService.withDoc(ref.docId, store => {
          const matches = store
            .getModelsByFlavour(REPEATABLE_FLAVOURS)
            .filter(model => readRepeat(model)?.id === seriesId)
            .filter(
              model =>
                !(skip && skip.docId === ref.docId && skip.blockId === model.id)
            );

          // Deleting a parent takes its children with it, so drop any block
          // that already has an ancestor in the set.
          const ids = new Set(matches.map(model => model.id));
          const roots = matches.filter(model => {
            let parent = store.getParent(model);
            while (parent) {
              if (ids.has(parent.id)) return false;
              parent = store.getParent(parent);
            }
            return true;
          });

          roots.forEach(model => store.deleteBlock(model));
          return roots.length;
        });
        if (count > 0) {
          removed += count;
          docs += 1;
        }
      } catch (error) {
        console.error(
          `Failed to clear repeats from journal ${ref.date}`,
          error
        );
      }
    }

    this.refreshSeriesIndex();
    return { removed, docs };
  }

  /** Moves a block out of its doc and into the journal for `date`. */
  async moveBlockToJournal(
    sourceDocId: string,
    blockId: string,
    date: string
  ): Promise<string> {
    const targetDocId = await this.ensureJournalReady(date);
    await this.docsService.copyBlocksToDoc(sourceDocId, [blockId], targetDocId);
    return targetDocId;
  }

  /**
   * Copies unchecked todos from the most recent earlier journal that has any.
   * Skips a gap day rather than giving up, and ignores lines that are already
   * part of a repeat series — those arrive on their own.
   */
  async carryUnfinishedTodos(params: {
    targetDocId: string;
    targetDate: string;
    parentId?: string;
    index?: number;
  }): Promise<{ carried: number; fromDate?: string }> {
    const { targetDocId, targetDate, parentId, index } = params;
    const earlier = this.journalDocs({ until: targetDate })
      .filter(ref => ref.date < targetDate)
      .reverse()
      .slice(0, CARRY_LOOKBACK_JOURNALS);

    for (const ref of earlier) {
      if (ref.docId === targetDocId) continue;
      let blockIds: string[] = [];
      try {
        blockIds = await this.docsService.withDoc(ref.docId, store =>
          store
            .getModelsByFlavour('affine:list')
            .filter(model => {
              const props = model.props as {
                type?: string;
                checked?: boolean;
              };
              return props.type === 'todo' && props.checked !== true;
            })
            .filter(model => !readRepeat(model))
            // Only top-level todos; children come along with their parent.
            .filter(model => {
              const parent = store.getParent(model);
              return !parent || parent.flavour === 'affine:note';
            })
            .map(model => model.id)
        );
      } catch (error) {
        console.error(`Failed to read journal ${ref.date} for carry`, error);
        continue;
      }

      if (blockIds.length === 0) continue;

      await this.docsService.copyBlocksToDoc(ref.docId, blockIds, targetDocId, {
        parentId,
        index,
      });
      return { carried: blockIds.length, fromDate: ref.date };
    }

    return { carried: 0 };
  }

  /**
   * Scans journals from today forward for repeat series. Only upcoming entries
   * are scanned — past occurrences are history, and ending a series only ever
   * affects what has not happened yet.
   */
  async scanSeries(): Promise<RepeatSeriesSummary[]> {
    if (this.scanPromise) return this.scanPromise;

    const run = async () => {
      const refs = this.journalDocs({ from: today() });
      const byId = new Map<string, RepeatSeriesSummary>();

      for (const ref of refs) {
        try {
          const found = await this.docsService.withDoc(ref.docId, store =>
            store
              .getModelsByFlavour(REPEATABLE_FLAVOURS)
              .map(model => ({ repeat: readRepeat(model), model }))
              .filter(
                (
                  entry
                ): entry is { repeat: BlockRepeatMeta; model: BlockModel } =>
                  !!entry.repeat
              )
              .map(({ repeat, model }) => ({
                repeat,
                text: model.text?.toString() ?? '',
              }))
          );

          for (const { repeat, text } of found) {
            const existing = byId.get(repeat.id);
            if (existing) {
              existing.count += 1;
              existing.lastDate = ref.date;
              if (!existing.text) existing.text = text;
            } else {
              byId.set(repeat.id, {
                id: repeat.id,
                rule: repeat.rule,
                anchor: repeat.anchor,
                text,
                count: 1,
                firstDate: ref.date,
                lastDate: ref.date,
              });
            }
          }
        } catch (error) {
          console.error(`Failed to scan journal ${ref.date}`, error);
        }
      }

      const summaries = [...byId.values()].sort((a, b) =>
        a.firstDate.localeCompare(b.firstDate)
      );
      this.seriesIndex$.next(summaries);
      return summaries;
    };

    this.scanPromise = run().finally(() => {
      this.scanPromise = null;
    });
    return this.scanPromise;
  }

  /** Fire-and-forget rescan used to keep {@link seriesIndex$} warm. */
  refreshSeriesIndex() {
    this.scanSeries().catch(console.error);
  }

  /** Kicks off a scan if one has never run. Safe to call from render paths. */
  ensureSeriesIndex() {
    if (this.seriesIndex$.value === null) this.refreshSeriesIndex();
  }
}

export type { BlockRepeatMeta };
